import {
  TechnicianKycStatus,
  TechnicianPosition,
  TechnicianStatus,
  TechnicianType,
} from '../utils/entities/type';
import { ITechnician, ProfessionalSkill } from '../utils/entities/interface';
import { AttendanceComputedStatus } from '../utils/attendancePolicy';
import { get, post, put } from './https';

interface Pagination {
  totalUsers: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface TechnicianListResponse {
  status: boolean;
  data: ITechnician[];
  count: number;
  pagination: Pagination;
}

export interface ITodayPresentAttendance {
  _id: string;
  technicianId?: string;
  createdAt: string;
  updatedAt: string;
  date?: string;
  technicianName: string;
  technicianType: string;
  technicianPosition?: string;
  technicianStatus: string;
  checkInTime?: string;
  checkOutTime?: string;
  pairedHelper?: string;
  pairedHelperName?: string | null;
  pairedHelperId?: string | null;
  isPaired?: boolean;
  totalMinutes?: number | null;
  workingStatus?: 'COMPLETED' | 'ONGOING' | 'MISSING_CHECKOUT' | 'N/A';
  status: 'PRESENT' | 'ABSENT' | 'LEAVE' | 'HOLIDAY' | string;
  computedStatus?: AttendanceComputedStatus | string;
}

interface TodayPresentAttendanceResponse {
  status?: boolean | string;
  message?: string;
  data: ITodayPresentAttendance[];
  count: number;
  pagination?: {
    totalTechnicians: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

const GETTechnicians = async ({
  page = 1,
  limit = 25,
  searchQuery = '',
  sortField = 'name',
  sortOrder = 'ASC',
  type,
  position,
  status,
  kycStatus,
  excludePosition,
  workEligible,
}: {
  page?: number;
  limit?: number;
  searchQuery?: string;
  sortField?: string;
  sortOrder?: 'ASC' | 'DESC';
  type?: TechnicianType | any;
  position?: TechnicianPosition | any;
  status?: TechnicianStatus | any;
  kycStatus?: TechnicianKycStatus | any;
  excludePosition?:string;
  workEligible?: boolean;
}): Promise<TechnicianListResponse> => {
  let url = `/api/v1/admin/technician/list?limit=${limit}&page=${page}`;

  console.log('TYPE', type);

  if (searchQuery) {
    url += `&search=${searchQuery}`;
  }

  if (sortField && sortField !== 'name') {
    url += `&sortField=${sortField}`;
  }

  if (excludePosition) {
    url += `&excludePosition=${excludePosition}`;
  }

  if (sortOrder && sortOrder !== 'ASC') {
    url += `&sortOrder=${sortOrder}`;
  }

  if (type) {
    url += `&type=${type}`;
  }

  if (position) {
    url += `&position=${position}`;
  }

  if (status) {
    url += `&status=${status}`;
  }

  if (kycStatus) {
    url += `&kycStatus=${kycStatus}`;
  }

  if (workEligible) {
    url += '&workEligible=true';
  }

  const response = await post(url);

  if (response.status == 200) {
    return response.data;
  } else {
    throw new Error('Failed to fetch users');
  }
};

const GETAvailableTechnicians = async ({
  page = 1,
  limit = 25,
  searchQuery = '',
  sortField = 'name',
  sortOrder = 'ASC',
  type,
  position,
  status,
  kycStatus,
}: {
  page?: number;
  limit?: number;
  searchQuery?: string;
  sortField?: string;
  sortOrder?: 'ASC' | 'DESC';
  type?: TechnicianType | any;
  position?: TechnicianPosition | any;
  status?: TechnicianStatus | any;
  kycStatus?: TechnicianKycStatus | any;
}): Promise<TechnicianListResponse> => {
  let url = `/api/v1/admin/technician/list/available?limit=${limit}&page=${page}`;

  console.log('TYPE', type);

  if (searchQuery) {
    url += `&search=${searchQuery}`;
  }

  if (sortField && sortField !== 'name') {
    url += `&sortField=${sortField}`;
  }

  if (sortOrder && sortOrder !== 'ASC') {
    url += `&sortOrder=${sortOrder}`;
  }

  if (type) {
    url += `&type=${type}`;
  }

  if (position) {
    url += `&position=${position}`;
  }

  if (status) {
    url += `&status=${status}`;
  }

  if (kycStatus) {
    url += `&kycStatus=${kycStatus}`;
  }

  const response = await get(url);

  if (response.status == 200) {
    return response.data;
  } else {
    throw new Error('Failed to fetch users');
  }
};

const GetTodayPresentAttendance = async ({
  page = 1,
  limit = 25,
  searchQuery = '',
  type,
  position,
  excludePosition,
  kycStatus,
  workEligible,
}: {
  page?: number;
  limit?: number;
  searchQuery?: string;
  type?: TechnicianType | any;
  position?: string;
  excludePosition?: string;
  kycStatus?: TechnicianKycStatus | any;
  workEligible?: boolean;
}): Promise<TodayPresentAttendanceResponse> => {
  let url = `/api/v1/admin/technician/today-present-attendance?limit=${limit}&page=${page}`;

  if (type) {
    url += `&type=${type}`;
  }

  if (position) {
    url += `&position=${position}`;
  }

  if (excludePosition) {
    url += `&excludePosition=${excludePosition}`;
  }

  if (kycStatus) {
    url += `&kycStatus=${kycStatus}`;
  }

  if (workEligible) {
    url += '&workEligible=true';
  }

  if (searchQuery) {
    url += `&search=${searchQuery}`;
  }

  const response = await get(url);

  if (response.status === 200) {
    const body = response.data;
    return {
      status: body?.status,
      message: body?.message,
      data: Array.isArray(body?.data) ? body.data : [],
      count: body?.count ?? 0,
      pagination: body?.pagination,
    };
  }

  throw new Error(
    response.data?.message ||
      response.statusText ||
      'Failed to fetch today present attendance',
  );
};

export interface ILastDayAbsentRecord {
  _id: string;
  technicianId: string;
  technicianName: string;
  technicianType: string;
  technicianPosition?: string;
  lastDayAbsent: string;
  absenceDuration: number;
  leaveStatus: 'NOT_APPLIED' | 'PENDING' | 'APPROVED' | 'REJECTED';
}

interface LastDayAbsentResponse {
  status?: boolean | string;
  message?: string;
  data: ILastDayAbsentRecord[];
  count: number;
  pagination?: {
    totalTechnicians: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

const GetLastDayAbsentAttendance = async ({
  page = 1,
  limit = 25,
  searchQuery = '',
  type,
  position,
  excludePosition,
  kycStatus,
  workEligible,
}: {
  page?: number;
  limit?: number;
  searchQuery?: string;
  type?: TechnicianType | any;
  position?: string;
  excludePosition?: string;
  kycStatus?: TechnicianKycStatus | any;
  workEligible?: boolean;
}): Promise<LastDayAbsentResponse> => {
  let url = `/api/v1/admin/technician/last-day-absent-attendance?limit=${limit}&page=${page}`;

  if (type) {
    url += `&type=${type}`;
  }

  if (position) {
    url += `&position=${position}`;
  }

  if (excludePosition) {
    url += `&excludePosition=${excludePosition}`;
  }

  if (kycStatus) {
    url += `&kycStatus=${kycStatus}`;
  }

  if (workEligible) {
    url += '&workEligible=true';
  }

  if (searchQuery) {
    url += `&search=${searchQuery}`;
  }

  const response = await get(url);

  if (response.status === 200) {
    const body = response.data;
    return {
      status: body?.status,
      message: body?.message,
      data: Array.isArray(body?.data) ? body.data : [],
      count: body?.count ?? 0,
      pagination: body?.pagination,
    };
  }

  throw new Error(
    response.data?.message ||
      response.statusText ||
      'Failed to fetch last day absent attendance',
  );
};

const ToggleTechnicianStatus = async (
  technicianId: string,
): Promise<number> => {
  const response = await post(
    `/api/v1/admin/technician/status?technicianId=${technicianId}`,
  );

  if (response.status == 200) {
    return 200;
  } else {
    throw new Error(
      response.statusText || 'Failed to change technician status',
    );
  }
};

const UpdateKycStatus = async (
  technicianId: string,
  kycStatus: 'APPROVE' | 'REJECT' | 'REQUEST',
): Promise<number> => {
  const url = `/api/v1/admin/technician/kycstatus`;
  const result = await post(url, { technicianId, action: kycStatus });

  if (result.status == 200) {
    return 200;
  } else {
    throw Error('Failed To Update KYC Status');
  }
};

const CreateTechnician = async ({
  phoneNumber,
  name,
  email,
  profilePhoto,
  joiningDate,
  type,
  position,
  secondaryContactNumber,
  dob,
  experience,
  professionalSkills,
  house,
  street,
  city,
  state,
  zipcode,
  landmark,
}: {
  phoneNumber: string;
  name: string;
  email: string;
  profilePhoto: string | null;
  joiningDate: Date;
  dob: Date;
  type: 'ACD' | 'FC';
  position: TechnicianPosition;
  secondaryContactNumber: string;
  experience: string;
  professionalSkills: ProfessionalSkill[];
  house: string;
  street: string;
  city: string;
  state: string;
  zipcode: string;
  landmark: string;
}): Promise<number> => {
  const payload: Record<string, any> = {
    name,
    type,
    countryCode: '+91',
    phoneNumber,
    position,
    house,
    street,
    city,
    state,
    zipcode,
    landmark,
  };

  if (secondaryContactNumber) {
    payload.secondaryContactNumber = secondaryContactNumber;
  }

  if (email) {
    payload.email = email;
  }

  if (profilePhoto) {
    payload.profilePhoto = profilePhoto;
  }

  if (experience) {
    payload.experience = experience;
  }

  if (joiningDate && !isNaN(new Date(joiningDate).getTime())) {
    payload.joiningDate = joiningDate;
  }

  if (dob && !isNaN(new Date(dob).getTime())) {
    payload.dob = dob;
  }

  if (Array.isArray(professionalSkills)) {
    payload.professionalSkills = professionalSkills;
  }

  console.log('CREATE TECHNICIAN PAYLOAD ===>', payload);

  const response = await post('/api/v1/admin/technician/create', payload);

  if (response.status == 201) {
    return 201;
  } else {
    throw new Error(response.statusText || 'Failed to create technician');
  }
};

const EditTechnician = async ({
  name,
  joiningDate,
  email,
  profilePhoto,
  technicianId,
  position,
  type,
  secondaryContactNumber,
  phoneNumber,
  dob,
  experience,
  house,
  street,
  city,
  state,
  zipcode,
  landmark,
}: {
  name: string;
  joiningDate: Date;
  email?: string;
  profilePhoto: string | null;
  technicianId: string;
  position?: TechnicianPosition;
  type: TechnicianType;
  phoneNumber: string;
  secondaryContactNumber: string;
  dob: Date;
  experience: string;
  house: string;
  street: string;
  city: string;
  state: string;
  zipcode: string;
  landmark: string;
}): Promise<number> => {
  const payload: Record<string, any> = {
    name,
    technicianId,
    type,
    countryCode: '+91',
    phoneNumber,
    experience,
    house,
    street,
    city,
    state,
    zipcode,
    landmark,
  };

  // Backend does not allow "TBA" in edit payload.
  // Skip sending position when it is not a valid persisted role.
  if (position && position !== 'TBA') {
    payload.position = position;
  }

  if (secondaryContactNumber) {
    payload.secondaryContactNumber = secondaryContactNumber;
  }

  if (email) {
    payload.email = email;
  }

  payload.profilePhoto = profilePhoto || '';

  if (joiningDate && !isNaN(new Date(joiningDate).getTime())) {
    payload.joiningDate = joiningDate;
  }

  if (dob && !isNaN(new Date(dob).getTime())) {
    payload.dob = dob;
  }

  const response = await post('/api/v1/admin/technician/edit', payload);

  if (response.status === 200) {
    return 200;
  } else {
    throw new Error(response.statusText || 'Failed to Edit technician');
  }
};

const GetTechnician = async (
  technicianId: string,
): Promise<{ technician: ITechnician; address: any }> => {
  const url = `/api/v1/admin/technician/${technicianId}`;
  const result = await get(url);

  if (result.status == 200) {
    return result.data.data;
  } else {
    throw Error('Failed to getting technician details');
  }
};

const AddDocument = async ({
  technicianId,
  comment,
  type,
  docUrl,
}: {
  technicianId: string;
  comment: string;
  type: string;
  docUrl: string;
}): Promise<number> => {
  const url = `/api/v1/admin/technician/kyc/manage/${technicianId}`;
  const result = await post(url, { comment, type, docUrl });

  if (result.status == 200) {
    return 200;
  } else {
    throw new Error(result.statusText || 'Failed to Add Documents');
  }
};

const TechnicianHistory = async (
  technicianId: string,
  startDate?: Date,
  endDate?: Date,
) => {
  let url = `/api/v1/admin/technician/self-booking/list/${technicianId}`;

  if (startDate && endDate) {
    url += `?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`;
  }

  const result = await get(url);

  if (result.status == 200 && Array.isArray(result.data.data)) {
    return result.data.data;
  } else {
    throw new Error(result.statusText || 'Failed to Get History');
  }
};

const CompleteProfile = async ({
  name,
  email,
  secondaryContactNumber,
  dob,
  technicianId,
}: {
  name: string;
  email: string;
  dob: Date;
  technicianId: string;
  secondaryContactNumber: string;
}): Promise<number> => {
  const payload: Record<string, any> = {
    name,
  };

  if (secondaryContactNumber) {
    payload.secondaryContactNumber = secondaryContactNumber;
  }

  if (email) {
    payload.email = email;
  }

  if (dob && !isNaN(new Date(dob).getTime())) {
    payload.dob = dob;
  }

  const response = await post(
    `/api/v1/admin/technician/profile/${technicianId}`,
    payload,
  );

  if (response.status == 200) {
    return 200;
  } else {
    throw new Error(response.statusText || 'Failed to create technician');
  }
};

interface UpdateProfessionalDetailsPayload {
  technicianId: string;
  professionalSkills: ProfessionalSkill[];
  position: string;
  exp: string;
}

const UpdateProfessionalDetails = async ({
  technicianId,
  professionalSkills,
  position,
  exp,
}: UpdateProfessionalDetailsPayload): Promise<number> => {
  const result = await put(
    `/api/v1/admin/technician/professional-skill/${technicianId}`,
    {
      professionalSkills,
      position,
      exp,
    },
  );

  if (result.status) {
    return 200;
  } else {
    throw new Error(result.statusText);
  }
};

export interface IAttendancePunch {
  kind: 'IN' | 'OUT';
  at: string; // ISO
  source?: 'TECH_APP' | 'ADMIN' | 'MIGRATION' | 'SYSTEM';
  note?: string;
}

export interface IAttendance {
  _id: string;
  technicianId: string;
  date: string;
  type:
    | 'PRESENT'
    | 'ABSENT'
    | 'LEAVE'
    | 'HOLIDAY'
    | 'LATE_CHECK_IN'
    | 'HALF_DAY'
    | 'WEEKLY_OFF';
  description: string;
  checkInTime?: string;
  checkOutTime?: string;
  totalMinutes?: number | null;
  workingStatus?: 'COMPLETED' | 'ONGOING' | 'MISSING_CHECKOUT' | 'N/A';
  workingTime?: number | 'ONGOING' | 'MISSING_CHECKOUT' | 'N/A';
  // Engine-derived fields (optional: absent in old backend responses)
  computedStatus?: AttendanceComputedStatus | null;
  statusSource?: 'ENGINE' | 'ADMIN' | 'LEGACY' | null;
  workedMinutes?: number | null;
  firstCheckIn?: string | null;
  lastCheckOut?: string | null;
  punches?: IAttendancePunch[];
  excusedByLeave?: boolean;
  createdAt: string;
  updatedAt: string;
}

const GetAttendance = async ({
  technicianId,
  startDate,
  endDate,
}: {
  technicianId: string;
  startDate: Date;
  endDate: Date;
}): Promise<IAttendance[]> => {
  const url = `/api/v1/admin/attendance/list?technicianId=${technicianId}&startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`;
  const result = await get(url);

  if (result.status === 200 && Array.isArray(result.data.data)) {
    return result.data.data as IAttendance[];
  } else {
    throw new Error(result.statusText || 'Failed to fetch attendance data');
  }
};

export type AttendanceEventType =
  | 'CHECK_IN'
  | 'CHECK_OUT'
  | 'PRESENT'
  | 'ABSENT'
  | 'LEAVE'
  | 'LEAVE_NO_INFO'
  | 'HOLIDAY'
  | 'SUNDAY'
  | 'LATE_COMING'
  | 'EARLY_GOING'
  | 'HALF_DAY'
  | 'LATE_CHECK_IN'
  | 'WEEKLY_OFF';

export interface IAttendanceDayEvent {
  type: AttendanceEventType;
  label: string;
}

export interface IAttendanceSpanEvent {
  id: string;
  type: AttendanceEventType;
  label: string;
  startDate: string;
  endDate: string;
}

export interface IAttendanceCalendarDay {
  date: string;
  dayIndex: number;
  dayName: string;
  status: string;
  isFuture: boolean;
  /** Today before its row is engine/admin-owned — excluded from payroll math */
  isPending?: boolean;
  holidayType?: string | null;
  holidayDescription?: string | null;
  checkInTime?: string | null;
  checkOutTime?: string | null;
  description?: string;
  events: IAttendanceDayEvent[];
  // Engine-derived (optional: absent in old backend responses)
  computedStatus?: string | null;
  statusSource?: string | null;
  workedMinutes?: number;
  excusedByLeave?: boolean;
  firstCheckIn?: string | null;
  lastCheckOut?: string | null;
  punches?: IAttendancePunch[];
}

export interface IAttendanceCalendarSummary {
  presentDays: number;
  absentDays: number;
  leavesTaken: number;
  leavesWithoutInfo: number;
  holiday: number;
  lateComing: number;
  earlyGoing: number;
  halfDay: number;
  sunday: number;
  // Engine-era additions (optional against old backend)
  weeklyOff?: number;
  halfDayWork?: number;
  excusedHalfDay?: number;
  workedMinutesTotal?: number;
}

export interface IAttendanceDeductionPreview {
  conversions: {
    lateHalfDayEquivalents: number;
    residualLates: number;
    totalHalfDayEquivalents: number;
    convertedAbsents: number;
    leftoverHalfDay: number;
  };
  deductions: {
    rawAbsentDays: number;
    conversionDeductionDays: number;
    leaveDeductionDays?: number;
    totalDeductionDays: number;
  };
  netPayableDays: number;
  /** Exact elapsed-day counts the conversion ran on (optional against old backend) */
  inputs?: {
    totalDaysInRange: number;
    elapsedDays: number;
    pendingToday: boolean;
    present: number;
    late: number;
    halfDay: number;
    excusedHalfDay: number;
    absent: number;
    leave: number;
    holiday: number;
    weeklyOff: number;
  };
}

export interface IAttendanceCalendarResponse {
  month: number;
  year: number;
  startDate: string;
  endDate: string;
  rangeLabel: string;
  weeklyOffDay?: number;
  summary: IAttendanceCalendarSummary;
  deductionPreview?: IAttendanceDeductionPreview;
  spanEvents: IAttendanceSpanEvent[];
  data: IAttendanceCalendarDay[];
}

const GetTechnicianAttendanceCalendar = async ({
  technicianId,
  month,
  year,
  startDate,
  endDate,
}: {
  technicianId: string;
  month?: number;
  year?: number;
  startDate?: string;
  endDate?: string;
}): Promise<IAttendanceCalendarResponse> => {
  const params = new URLSearchParams();

  if (startDate && endDate) {
    params.set('startDate', startDate);
    params.set('endDate', endDate);
  } else if (month && year) {
    params.set('month', String(month));
    params.set('year', String(year));
  }

  const url = `/api/v1/admin/technician/leave-calendar/${technicianId}?${params.toString()}`;
  const result = await get(url);

  if (result.status === 200 && result.data?.status) {
    return {
      month: result.data.month,
      year: result.data.year,
      startDate: result.data.startDate,
      endDate: result.data.endDate,
      rangeLabel: result.data.rangeLabel,
      weeklyOffDay: result.data.weeklyOffDay,
      summary: result.data.summary,
      deductionPreview: result.data.deductionPreview,
      spanEvents: result.data.spanEvents || [],
      data: result.data.data,
    };
  }

  throw new Error(
    result.statusText || 'Failed to fetch technician attendance calendar',
  );
};

const MarkAttendance = async ({
  technicianId,
  date,
  type,
  description,
}: {
  technicianId: string;
  date: string;
  type: 'PRESENT' | 'ABSENT' | 'LEAVE' | 'HOLIDAY';
  description: string;
}) => {
  const EPURL = `/api/v1/admin/attendance/mark/${technicianId}`;

  const result = await post(EPURL, {
    date,
    type,
    description,
  });

  console.log('MARK ATTENDANCE RESULT', result);

  if (result.status === 200) {
    return 200;
  } else {
    throw new Error(result.statusText || 'Failed to mark attendance');
  }
};

/**
 * Soft delete a technician
 */
const SoftDeleteTechnician = async (technicianId: string): Promise<number> => {
  const url = `/api/v1/admin/technician/soft-delete`;
  const result = await post(url, { technicianId });

  if (result.status === 200) {
    return 200;
  } else {
    throw new Error(result.data?.message || 'Failed to delete technician');
  }
};

/**
 * Restore a soft-deleted technician
 */
const RestoreTechnician = async (technicianId: string): Promise<number> => {
  const url = `/api/v1/admin/technician/restore`;
  const result = await post(url, { technicianId });

  if (result.status === 200) {
    return 200;
  } else {
    throw new Error(result.data?.message || 'Failed to restore technician');
  }
};

/**
 * Bulk soft delete technicians
 */
const BulkDeleteTechnicians = async (technicianIds: string[]): Promise<{ deletedCount: number }> => {
  const url = `/api/v1/admin/technician/bulk-delete`;
  const result = await post(url, { technicianIds });

  if (result.status === 200) {
    return { deletedCount: result.data?.deletedCount || 0 };
  } else {
    throw new Error(result.data?.message || 'Failed to delete technicians');
  }
};

/**
 * Update attendance record for any day (type, times, description)
 */
const UpdateAttendanceRecord = async ({
  technicianId,
  date,
  type,
  description,
  checkInHour,
  checkInMinute,
  checkoutHour,
  checkoutMinute,
  clearCheckout,
}: {
  technicianId: string;
  date: string;
  type: 'PRESENT' | 'ABSENT' | 'LEAVE' | 'HOLIDAY' | 'WEEKLY_OFF';
  description?: string;
  checkInHour?: number;
  checkInMinute?: number;
  checkoutHour?: number;
  checkoutMinute?: number;
  clearCheckout?: boolean;
}) => {
  const EPURL = `/api/v1/admin/attendance/${technicianId}`;

  const result = await put(EPURL, {
    date,
    type,
    description: description ?? '',
    ...(checkInHour !== undefined ? { checkInHour } : {}),
    ...(checkInMinute !== undefined ? { checkInMinute } : {}),
    ...(checkoutHour !== undefined ? { checkoutHour } : {}),
    ...(checkoutMinute !== undefined ? { checkoutMinute } : {}),
    ...(clearCheckout ? { clearCheckout: true } : {}),
  });

  if (result?.status) {
    return result.data;
  }

  throw new Error(result?.message || 'Failed to update attendance');
};

/**
 * Update checkout time for a technician's attendance
 * @param technicianId - Technician ID
 * @param date - Date in DD-MM-YYYY format
 * @param checkoutHour - Hour (0-23) for checkout time
 * @param checkoutMinute - Minute (0-59) for checkout time
 */
const UpdateCheckoutTime = async ({
  technicianId,
  date,
  checkoutHour,
  checkoutMinute,
}: {
  technicianId: string;
  date: string;
  checkoutHour: number;
  checkoutMinute: number;
}) => {
  const EPURL = `/api/v1/admin/attendance/checkout/${technicianId}`;

  const result = await post(EPURL, {
    date,
    checkoutHour,
    checkoutMinute,
  });

  if (result.status === 200) {
    return result.data;
  } else {
    throw new Error(result.data?.message || 'Failed to update checkout time');
  }
};

export interface IAttendancePeriodSummary {
  _id: string;
  technicianId: string;
  year: number;
  month: number;
  counts: {
    present: number;
    lateCheckIn: number;
    halfDay: number;
    excusedHalfDay: number;
    absent: number;
    leave: number;
    holiday: number;
    weeklyOff: number;
    elapsedDays: number;
    workingDays: number;
  };
  conversions: {
    lateHalfDayEquivalents: number;
    residualLates: number;
    totalHalfDayEquivalents: number;
    convertedAbsents: number;
    leftoverHalfDay: number;
  };
  deductions: {
    rawAbsentDays: number;
    conversionDeductionDays: number;
    totalDeductionDays: number;
  };
  netPayableDays: number;
  computedAt?: string | null;
  isFinal: boolean;
  technician?: {
    name: string;
    phoneNumber?: string;
    type?: string;
    position?: string;
  };
}

const GetAttendancePeriodSummary = async ({
  technicianId,
  year,
  month,
}: {
  technicianId: string;
  year: number;
  month: number;
}): Promise<IAttendancePeriodSummary> => {
  const url = `/api/v1/admin/attendance/period-summary/${technicianId}?year=${year}&month=${month}`;
  const result = await get(url);

  if (result.status === 200 && result.data?.status) {
    return result.data.data as IAttendancePeriodSummary;
  }
  throw new Error(result.statusText || 'Failed to fetch period summary');
};

const GetAttendancePeriodSummaryList = async ({
  year,
  month,
  page = 1,
  limit = 20,
  search,
  hasDeduction,
}: {
  year: number;
  month: number;
  page?: number;
  limit?: number;
  search?: string;
  hasDeduction?: boolean;
}): Promise<{
  items: IAttendancePeriodSummary[];
  pagination: { total: number; page: number; limit: number; totalPages: number };
}> => {
  const params = new URLSearchParams({
    year: String(year),
    month: String(month),
    page: String(page),
    limit: String(limit),
  });
  if (search) params.set('search', search);
  if (hasDeduction) params.set('hasDeduction', 'true');

  const url = `/api/v1/admin/attendance/period-summary?${params.toString()}`;
  const result = await get(url);

  if (result.status === 200 && result.data?.status) {
    return result.data.data;
  }
  throw new Error(result.statusText || 'Failed to fetch period summaries');
};

export interface IAttendanceAuditEntry {
  _id: string;
  attendanceId?: string | null;
  technicianId: string;
  date: string;
  action:
    | 'STATUS_CHANGE'
    | 'PUNCH_ADDED'
    | 'PUNCH_EDITED'
    | 'MANUAL_OVERRIDE'
    | 'FINALIZED';
  fromStatus?: string | null;
  toStatus?: string | null;
  changedBy?: {
    kind: 'SYSTEM' | 'ADMIN' | 'TECHNICIAN';
    id?: { name?: string; email?: string } | string | null;
  };
  meta?: Record<string, unknown> | null;
  createdAt: string;
}

const GetAttendanceAudit = async ({
  technicianId,
  date,
  from,
  to,
  page = 1,
  limit = 50,
}: {
  technicianId: string;
  date?: string; // DD-MM-YYYY
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}): Promise<{
  items: IAttendanceAuditEntry[];
  pagination: { total: number; page: number; limit: number; totalPages: number };
}> => {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (date) params.set('date', date);
  if (from && to) {
    params.set('from', from);
    params.set('to', to);
  }
  const url = `/api/v1/admin/attendance/audit/${technicianId}?${params.toString()}`;
  const result = await get(url);

  if (result.status === 200 && result.data?.status) {
    return result.data.data;
  }
  throw new Error(result.statusText || 'Failed to fetch attendance audit');
};

export {
  GETTechnicians,
  ToggleTechnicianStatus,
  CreateTechnician,
  GetTechnician,
  EditTechnician,
  UpdateKycStatus,
  AddDocument,
  TechnicianHistory,
  GETAvailableTechnicians,
  CompleteProfile,
  GetAttendance,
  GetTechnicianAttendanceCalendar,
  MarkAttendance,
  UpdateAttendanceRecord,
  UpdateCheckoutTime,
  UpdateProfessionalDetails,
  GetTodayPresentAttendance,
  GetLastDayAbsentAttendance,
  SoftDeleteTechnician,
  RestoreTechnician,
  BulkDeleteTechnicians,
  GetAttendancePeriodSummary,
  GetAttendancePeriodSummaryList,
  GetAttendanceAudit,
};
